import { Autocomplete } from "@mui/lab";
import {
	Button,
	CircularProgress,
	Grid2,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import MesfModal from "../../../../components/modals/modal.mesf";
import { themeDXT } from "../../../../css/themeMESF";
import { HelmetDexteel } from "../../../../helmet/HelmetDexteel";
import {
	getQueryCacheInvalidations,
	upsertQueryCacheInvalidations,
} from "../repositories/config-repository";

export interface QueryCacheResponse {
	actions: Action[];
	cacheInvalidations: CacheInvalidation[];
}

export interface Action {
	ActionId: number;
	ActionName: string;
	ActionTypeId: number;
}

export interface CacheInvalidation {
	SourceId: number;
	Source: string;
	TargetsCSV: string;
}

interface FormData {
	source: Action | null;
	targets: Action[];
}

export const searchQueryCacheInvalidations = () => {
	return useQuery<QueryCacheResponse, Error>({
		queryKey: ["queryCacheInvalidations"],
		queryFn: ({ signal }) => getQueryCacheInvalidations(signal),
	});
};

const QueryCacheInvalidations = () => {
	const gridRef = useRef<AgGridReact>(null);
	const [rowData, setRowData] = useState<CacheInvalidation[]>([]);
	const [actions, setActions] = useState<Action[]>([]);
	const [modal, setModal] = useState<"add" | "edit" | "">("");
	const [selectedRow, setSelectedRow] = useState<CacheInvalidation | null>(
		null,
	);
	const [notification, setNotification] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const { control, reset, handleSubmit } = useForm<FormData>({
		defaultValues: {
			source: null,
			targets: [],
		},
	});

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	const columnDefs: ColDef[] = [
		{ field: "SourceId", headerName: "Source ID", flex: 1 },
		{ field: "Source", headerName: "Source", flex: 2 },
		{ field: "TargetNamesCSV", headerName: "Targets", flex: 3 },
		{
			headerName: "Actions",
			flex: 1,
			cellRenderer: (params: { data: CacheInvalidation }) => (
				<Button
					variant="contained"
					color="primary"
					onClick={() => handleEdit(params.data)}
					style={{
						width: "100%",
						height: "28px",
						minWidth: "60px",
					}}
				>
					EDIT
				</Button>
			),
		},
	];

	const {
		data: rows,
		isLoading,
		isSuccess,
		isError,
		error: e,
		isFetched,
		refetch,
	} = searchQueryCacheInvalidations();

	const handleAdd = () => {
		reset({
			source: null,
			targets: [],
		});
		setModal("add");
	};

	const handleEdit = (row: CacheInvalidation) => {
		setSelectedRow(row);
		const targetsIds = row.TargetsCSV.split(",").map(Number);
		reset({
			source: actions.find((a) => a.ActionId === row.SourceId) || null,
			targets: actions.filter((a) => targetsIds.includes(a.ActionId)),
		});
		setModal("edit");
	};

	const upsertQueryCache = useMutation({
		mutationFn: upsertQueryCacheInvalidations,
		onSuccess: async () => {
			setNotification("Operation successful");
			await refetch();
		},
		onError: (error: Error) => {
			setNotification("Operation failed");
		},
		onSettled: () => {
			setModal("");
			setIsSubmitLoading(false);
		},
	});

	const onSubmit = async (data: FormData) => {
		if (!data.source) return;

		setIsSubmitLoading(true);
		await upsertQueryCache.mutate({
			actionId: data.source.ActionId,
			targets: data.targets,
		});
	};

	useEffect(() => {
		if (isSuccess && isFetched) {
			setRowData(rows.cacheInvalidations);
			setActions(rows.actions);
		}
	}, [isSuccess, isFetched, rows]);

	useEffect(() => {
		if (e && isError) {
			setNotification("Failed to fetch data");
			console.error("Fetch error:", e.message);
		}
	}, [e, isError]);

	return (
		<>
			<HelmetDexteel title="Query Cache Invalidations - Real Time" />
			{["add", "edit"].includes(modal) && (
				<HelmetDexteel
					title={`${
						modal === "add"
							? "Add Cache Invalidation"
							: "Edit Cache Invalidation"
					} - Query Cache Invalidations`}
				/>
			)}
			<Grid2
				container
				justifyContent="flex-start"
				alignItems="center"
				p={1}
				spacing={1}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Query Cache Invalidations
					</Typography>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Grid2
							component={Paper}
							size={{ md: 12, xs: 12 }}
							style={{
								height: "70vh",
							}}
						>
							<AgGridReact<CacheInvalidation>
								loading={isLoading}
								gridOptions={{
									theme: themeDXT,
								}}
								ref={gridRef}
								rowData={rowData}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								rowSelection="single"
								onRowDoubleClicked={(event) => {
									handleEdit(event.data as CacheInvalidation);
								}}
							/>
						</Grid2>
						<Grid2
							container
							justifyContent="flex-end"
							style={{ marginTop: 10 }}
						>
							<Grid2 size={{ md: 2, xs: 12 }}>
								<Button
									variant="contained"
									color="primary"
									onClick={handleAdd}
									fullWidth
								>
									ADD NEW
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</Grid2>

				<MesfModal
					title={
						modal === "add"
							? "Add Cache Invalidation"
							: "Edit Cache Invalidation"
					}
					open={["add", "edit"].includes(modal)}
					handleClose={() => setModal("")}
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<MesfModal.Content dividers>
							<Grid2 container spacing={3}>
								<Grid2 size={{ xs: 12 }}>
									<Controller
										control={control}
										name={"source"}
										render={(params) => (
											<Autocomplete<Action>
												options={actions}
												getOptionLabel={(option) => `${option.ActionName}`}
												value={params.field.value}
												onChange={(_, value) => params.field.onChange(value)}
												renderInput={(params: any) => (
													<TextField
														{...params}
														label="Select Source Action"
														variant="outlined"
														size="medium"
													/>
												)}
												disabled={modal === "edit"}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12 }}>
									<Controller
										control={control}
										name={"targets"}
										render={(params) => (
											<Autocomplete<Action, true, true, true>
												multiple
												options={actions.filter(
													(a) =>
														a.ActionId !== control._formValues.source?.ActionId,
												)}
												getOptionLabel={(option) => {
													if (typeof option === "string") {
														return option;
													}
													return option.ActionName;
												}}
												value={params.field.value}
												onChange={(_, value) => {
													params.field.onChange(value);
												}}
												renderInput={(params: any) => (
													<TextField
														{...params}
														label="Select Target Actions"
														variant="outlined"
														size="medium"
													/>
												)}
											/>
										)}
									/>
								</Grid2>
							</Grid2>
						</MesfModal.Content>
						<MesfModal.Actions>
							<Grid2 container spacing={2} justifyContent="flex-end">
								<Grid2 size={{ md: 3, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => setModal("")}
									>
										Cancel
									</Button>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 12 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color={"primary"}
										type="submit"
									>
										Save
									</Button>
								</Grid2>
							</Grid2>
						</MesfModal.Actions>
					</form>
				</MesfModal>
			</Grid2>
		</>
	);
};

export default QueryCacheInvalidations;
